import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { PageHead, ScopeFilters, qs, usePeople } from "@/lib/crm";
import { money } from "@/lib/pricing";

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-slate-800 p-5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p data-testid={testId} className="mt-1 font-mono-num text-3xl font-semibold text-slate-50">{value}</p>
    </div>
  );
}

function SalesTable({ title, rows, months, testId }) {
  return (
    <section className="overflow-x-auto rounded-2xl border border-slate-800 p-5" data-testid={testId}>
      <h2 className="mb-3 font-display text-xl text-slate-50">{title}</h2>
      {rows.length === 0 ? <p className="text-sm text-slate-500">No approved estimates in this period.</p> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500"><th className="py-2">Name</th>{months.map((m) => <th key={m} className="py-2 text-right">{m}</th>)}<th className="py-2 text-right">Total</th></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i} data-testid={`${testId}-row-${i}`} className="border-t border-slate-800/60">
              <td className="py-2 text-slate-200">{r.name}</td>
              {months.map((m) => <td key={m} className="py-2 text-right font-mono-num text-slate-300">{r.months[m] ? money(r.months[m]) : "—"}</td>)}
              <td className="py-2 text-right font-mono-num font-semibold text-slate-100" data-testid={`${testId}-total-${i}`}>{money(r.total)}</td>
            </tr>))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default function Reports() {
  const people = usePeople();
  const [f, setF] = useState({});
  const [d, setD] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get(`/reports?${qs({ ...f, q: "" })}`).then((r) => setD(r.data)).catch((e) => toast.error(errorMessage(e))), 200);
    return () => clearTimeout(t);
  }, [f]);
  const max = Math.max(1, ...(d?.pipeline || []).map((p) => p.value));
  return (
    <div className="space-y-8" data-testid="reports-page">
      <PageHead kicker="CRM" title="Reports" sub={people.me.role === "rep" ? "Your own numbers." : people.me.role === "manager" ? "Your showroom's numbers." : "All showrooms, or filter by one."} />
      <ScopeFilters people={people} f={f} setF={setF} prefix="reports" />
      {d && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Approval rate (approved ÷ sent)" testId="report-approval-rate" value={d.approval_rate == null ? "—" : `${Math.round(d.approval_rate * 1000) / 10}%`} />
            <Stat label="Sent / approved" testId="report-sent-approved" value={`${d.sent} / ${d.approved}`} />
            <Stat label="Unpaid invoices" testId="report-unpaid-total" value={money(d.unpaid_total)} />
          </div>
          <section className="rounded-2xl border border-slate-800 p-5" data-testid="report-pipeline">
            <h2 className="mb-3 font-display text-xl text-slate-50">Pipeline by stage</h2>
            <div className="space-y-2">{d.pipeline.map((p) => (
              <div key={p.stage} data-testid={`report-pipeline-${p.stage}`} className="grid grid-cols-[160px_1fr_160px] items-center gap-3 text-sm">
                <span className="text-slate-300">{p.label}</span>
                <span className="h-3 rounded-full bg-slate-900/40"><span className="block h-3 rounded-full bg-amber-500" style={{ width: `${(p.value / max) * 100}%` }} /></span>
                <span className="text-right font-mono-num text-slate-200">{p.count} · {money(p.value)}</span>
              </div>))}
            </div>
          </section>
          <div className="grid gap-4 xl:grid-cols-2">
            <SalesTable title="Sales by showroom (approved value)" rows={d.sales_by_showroom} months={d.months} testId="report-sales-showroom" />
            <SalesTable title="Sales by rep (approved value)" rows={d.sales_by_rep} months={d.months} testId="report-sales-rep" />
          </div>
          <div className="grid gap-4 xl:grid-cols-2">
            <section className="rounded-2xl border border-slate-800 p-5" data-testid="report-avg-days">
              <h2 className="mb-3 font-display text-xl text-slate-50">Average days per stage</h2>
              {d.avg_days.map((a) => <p key={a.stage} data-testid={`report-avg-${a.stage}`} className="flex justify-between border-t border-slate-800/60 py-1.5 text-sm"><span className="text-slate-300">{a.label}</span><span className="font-mono-num text-slate-100">{a.avg == null ? "—" : `${a.avg} d`} <span className="text-xs text-slate-500">({a.n})</span></span></p>)}
            </section>
            <section className="rounded-2xl border border-slate-800 p-5" data-testid="report-unpaid-list">
              <h2 className="mb-3 font-display text-xl text-slate-50">Unpaid invoices</h2>
              {d.unpaid.length === 0 && <p className="text-sm text-slate-500">Nothing outstanding.</p>}
              {d.unpaid.map((i, k) => <p key={i.id} data-testid={`report-unpaid-${k}`} className="flex justify-between border-t border-slate-800/60 py-1.5 text-sm"><span className="text-slate-300">{i.number} · {i.customer_name}{i.overdue ? " · overdue" : ""}</span><span className="font-mono-num text-slate-100">{money(i.balance)}</span></p>)}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
